import type { SceneConfig } from './sceneConfig';
import { resolveCdnUrl } from '../utils/cdnUrl';
import { MINA_DEFAULT_COLLIDERS } from './collision/minaColliders';

const MINA_2M_URL = resolveCdnUrl('models/mina/Mina_v4(2M).sog');
const MINA_1M_URL = resolveCdnUrl('models/mina/Mina_v4(1M).sog');
const MINA_600K_URL = resolveCdnUrl('models/mina/Mina_v4(600K).sog');

const TARGET_POINT = { x: -2.43, y: -1.54, z: -4.25 };

export const minaSceneConfig: SceneConfig = {
  id: 'mina',
  renderer: {
    clearColor: 0x0f0f13,
  },
  camera: {
    previewTourEnterDuration: 1.5,   // секунд: initialPosition → previewTourStart
    // previewTourExitDuration: 1.5,    // секунд: orbit → previewTourEnd.position
    fov: 80,
    near: 0.01,
    far: 1e4,
    initialPosition: { x: -2.16, y: -1.35, z: -3.4 },
    previewTourStart: { x: -2.34, y: 0.79, z: 1.84 },
    previewTourEnd: {
      position: { x: 2.35, y: 0.38, z: -1.74 },
      target: TARGET_POINT
    },

    flyover: {
      delay: 1,
      segments: [
        { type: 'waypoint', position: { x: -0.055, y: -1.337, z: -4.027 }, target: { x: -2.43, y: -1.54, z: -4.25 }, duration: 3 },
        { type: 'waypoint', position: { x: -3.113, y: -1.232, z: -1.112 }, target: { x: -4.127, y: -1.503, z: -1.972 }, duration: 3 },
        { type: 'waypoint', position: { x: 2.577, y: 0.632, z: 2.12 }, target: { x: -2.43, y: -1.54, z: -4.25 }, duration: 2 },
      ],
    },

    // flyover: {
    //   delay: 1,
    //   segments: [
    //     { type: 'waypoint', position: { x: -0.055, y: -1.337, z: -4.027 }, target: { x: -2.43, y: -1.54, z: -4.25 }, duration: 3 },
    //     { type: 'waypoint', position: { x: -4.413, y: -0.824, z: 0.853 }, target: { x: -2.43, y: -1.54, z: -4.25 }, duration: 3 },
    //     { type: 'waypoint', position: { x: 2.577, y: 0.632, z: 2.12 }, target: { x: -2.43, y: -1.54, z: -4.25 }, duration: 2 },
    //   ],
    // },
  },
  poi: {
    clusterEnabled: false,
  },
  controls: {
    orbitMinDistance: 0,
    orbitMaxDistance: 10000,
    clampMinHeight: -2,
    clampMaxHeight: Infinity,
    clampMaxHeightFar: Infinity,
    orbitMinPolarAngle: (70 / 180) * Math.PI,
    orbitMaxPolarAngle: Math.PI,
    // Mina is FPS-only: user can only drag-to-look and click POIs. Constrain
    // vertical to ~30°..150° so drag-to-look can't end up staring at the sky
    // or floor. Switch happens after the preview-tour terminates.
    forcedViewMode: 'first-person',
    constrainVertical: true,
    verticalMin: Math.PI / 6,
    verticalMax: (5 * Math.PI) / 6,
    // FPS-touch tuning. Look math normalizes by viewport width, so the same
    // value feels ~5x faster on mobile (~390px) vs desktop (~1920px) — see
    // CameraControlsOverrides.lookSpeedHorizontal doc.
    lookSpeedHorizontal: 120,
    lookSpeedVertical: 80,
    lookSpeedHorizontalMobile: 60,
    lookSpeedVerticalMobile: 40,
    orbitCenter: TARGET_POINT,
  },
  colliders: MINA_DEFAULT_COLLIDERS,
  splat: {
    defaultByQuality: {
      desktop: 'mina_2m',
      mobile: {
        high: 'mina_1m',
        middle: 'mina_1m',
        low: 'mina_600k',
      },
    },
    variants: [
      {
        key: 'mina_2m',
        url: MINA_2M_URL,
      },
      {
        key: 'mina_1m',
        url: MINA_1M_URL,
      },
      {
        key: 'mina_600k',
        url: MINA_600K_URL,
      },
    ],
    revealAnimationDuration: 2.5,
    revealDistanceScale: 2.0,
    revealMode: '3d',
    revealOrigin: TARGET_POINT,
  },
  previewModel: {
    url: resolveCdnUrl('preview/Mina_preloader_v2_pnts_150000_meshopt(16).glb'),
    style: {
      pointSizeFactor: 2.5,
      appearanceDuration: 1.5,
      wavesCenter: TARGET_POINT,
      wavesAmplitude: 2.0,
      wavesPeriod: 1.0,
      wavesSpeed: 1.5,
      wavesColorIntensity: 6.0,
      wavesDisplacement: 0.02,
    },
  },
  assistant: {
    position: { x: 0, y: 0, z: -0.03 },
    rotationY: 0,
    scale: 0.0000625,
    reflectionResolution: 128,
  },
  environment: {
    hemisphereLight: {
      skyColor: 0xffffff,
      groundColor: 0x444444,
      intensity: 0.6,
    },
    directionalLights: [
      {
        color: 0xffffff,
        intensity: 1.0,
        position: { x: 50, y: 100, z: 50 },
      },
    ],
    background: {
      color: 0x0f0f13,
      skybox: {
        url: resolveCdnUrl('environment/autumn_field_puresky_8k.jpg'),
        mobileUrl: resolveCdnUrl('environment/autumn_field_puresky_2k.jpg'),
        intensity: 1.0,
        blur: 0.0,
        rotation: [0, 205, 0],
      },
    },
  },
  virtualTour: [],
};
